import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useWatch } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router";

import { Dropdown, Form, SubmitButton, useForm } from "capitalrx-components-2.0/lib/forms";
import { Icon, Message, Messages, Spinner, TabSet, Tag, Tooltip } from "capitalrx-components-2.0/lib/general";
import { Stack, Shelf, Grid, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Heading, Subheading, Title } from "capitalrx-components-2.0/lib/typography";
import { CalculateMedicalServiceMaximumUsageResponse } from "capitalrx-types";

import { createMedicalClaim, getMedicalEligibilityAPI, getPACaseList } from "@/api";
import { MedicalBenefitRequest, MedicalBenefitResponse, MedicalEligibilityResponse, MedicalEligibilityRequest } from "@/api/interfaces/Medical";
import { DiagnosisCodeMultiSelect, ProcedureCodeDropdown, ServiceCategoryDropdown } from "@/components";
import { MemberSearchErrors, placeOfServiceOptions, PROVIDER_MANAGEMENT_LAYOUT, STANDARD_RESPONSIVE_LAYOUT } from "@/constants";
import { ThemeSelectorContext, AuthenticationContext } from "@/context";
import { ANALYTIC_EVENTS, logEvent } from "@/service/analyticsService";
import { capitalize, getThemeButton, getThemeURL, isProduction, isPrime } from "@/utils";

import { PatientForm } from "../PatientLookupForm/PatientLookupForm";
import styles from "./BenefitSearchForm.module.scss";
import { ResultPanel } from "./ResultPanel";
import { ServiceMaxResultPanel } from "./ServiceMaxResultPanel";
import { Accumulations } from "./Accumulations";

import { DrugCoverage } from "./DrugCoverage";
import { PACaseListSearch } from "./PACaseListSearch";
import { PACaseSearch } from "./PACaseSearch";

export interface BenefitSearchForm {
  readonly procedure_code: string;
  readonly diagnosis_codes: string[];
  readonly place_of_service: string;
  readonly service_category: string;
}

export const RESPONSIVE_LAYOUT = {
  Layout: {
    desktop: Grid,
    mobile: Stack,
  },
};

const layoutWidths = {
  layoutWidth: {
    desktop: "902px",
    tablet: "598px",
    mobile: "320px",
  },
};

type FlattenedServiceData = {
  service: string;
  network: string;
  type: string;
  remaining: number;
  maximum: number;
};

function flattenServiceData(data: CalculateMedicalServiceMaximumUsageResponse): FlattenedServiceData[] {
  const result = [];
  const networkMapping = {
    "in_network_tier_1": "In-network",
    "out_of_network": "out-of-network",
  };

  if (data) {
    Object.entries(data).forEach(([service, serviceData]) => {
      Object.entries(serviceData).forEach(([networkKey, usageData]) => {
        if (networkKey === "combined_networks") {
          const humanReadableNetworks = usageData.networks.map((networkName) => networkMapping[networkName]);
          const formattedNetworks = humanReadableNetworks.sort().join(", ");
          result.push({
            service,
            network: formattedNetworks,
            type: capitalize(usageData.type),
            remaining: usageData.remaining,
            maximum: usageData.maximum,
          });
        } else {
          result.push({
            service,
            network: networkMapping[networkKey],
            type: capitalize(usageData.type),
            remaining: usageData.remaining,
            maximum: usageData.maximum,
          });
        }
      });
    });
  }

  return result;
}
export const BenefitSearchForm: FC = () => {
  const { primary_color } = useContext(ThemeSelectorContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const form = useForm<BenefitSearchForm>();
  // const pa_search_form = useForm<CaseSearchForm>();

  const featureFlagHideProdAndPrime = !isProduction && !isPrime; // Only when env is anything but those two does it show the tabs
  const [loading, setLoading] = useState<boolean>(true);
  const [member, setMember] = useState<MedicalEligibilityResponse>();
  const [diagnosis_codes, setDiagnosisCodes] = useState<string[]>([]);

  const [results, setResults] = useState<MedicalBenefitResponse[]>([]);
  const [serviceMaximumResults, setServiceMaximumResults] = useState<Record<string, any>>({});
  const [serviceCategory, setServiceCategory] = useState<string>("");
  const [emptyResultMessage, setEmptyResultMessage] = useState<string | null>(null);
  const [lastPayload, setLastPayload] = useState<MedicalBenefitRequest | MedicalEligibilityRequest | null>(null);
  const [selectedTab, setSelectedTab] = useState<string | null>(
    searchParams.get("tab") ?? (featureFlagHideProdAndPrime ? "pa-cases" : "coverage-check"),
  );
  const [isMobileTabsExpanded, setIsMobileTabsExpanded] = useState(false);

  const { Layout } = useResponsive(STANDARD_RESPONSIVE_LAYOUT);
  const { fourFull, isCompressed } = useResponsive(PROVIDER_MANAGEMENT_LAYOUT);
  const { layoutWidth } = useResponsive(layoutWidths);

  const [tag, setTag] = useState<JSX.Element | null>(null);

  const [npi, setNpi] = useState<string>("");
  const [error, setError] = useState<string>();

  const [isProcedureCodeSelected, setisProcedureCodeSelected] = useState(false);
  const [isServiceCategorySelected, setIsServiceCategorySelected] = useState(false);
  const serviceCatField = useWatch({ name: "service_category", control: form.control });
  const procedureCodeField = useWatch({ name: "procedure_code", control: form.control });
  const { isUserLoggedIn, getAuthToken, getUser } = useContext(AuthenticationContext);
  const [hasActiveAccount, setHasActiveAccount] = useState(false);

  const [paResults, setPAResults] = useState([]);
  const [pageNumber, setPageNumber] = useState(0);

  const tabOptions = useMemo(() => {
    const options: { id: string; label: string }[] = [
      { id: "accumulations", label: "Accumulations" },
      { id: "drug-coverage", label: "Drug Coverage" },
      { id: "pa-cases", label: "PA Cases" },
    ];

    if (member?.is_medical_member) {
      options.unshift({ id: "coverage-check", label: "Service Coverage" });
    }

    return options;
  }, [member?.is_medical_member]);

  const selectedTabLabel = useMemo(() => {
    return tabOptions.find((option) => option.id === selectedTab)?.label || tabOptions[0]?.label || "";
  }, [selectedTab, tabOptions]);

  const getEncodedMemberParams = useCallback((tab: string) => {
    const params = {
      provider_npi: searchParams.get("provider_npi"),
      external_member_id: searchParams.get("external_member_id"),
      member_date_of_birth: searchParams.get("member_date_of_birth"),
      member_first: searchParams.get("member_first"),
      member_last: searchParams.get("member_last"),
      tab,
    };
    const encodedParams = new URLSearchParams();
    for (const key in params) {
      if (params[key]) {
        encodedParams.append(key, params[key]);
      }
    }
    return encodedParams;
  }, [searchParams]);

  const paCaseListUrl = useMemo(() => (
    `${getThemeURL("PATIENT_COVERAGE_SEARCH")}?${getEncodedMemberParams("pa-cases").toString()}`
  ), [getEncodedMemberParams]);

  useEffect(() => {
    if (serviceCatField) {
      setisProcedureCodeSelected(false);
      setIsServiceCategorySelected(true);
    } else if (procedureCodeField) {
      setisProcedureCodeSelected(true);
      setIsServiceCategorySelected(false);
    } else {
      setisProcedureCodeSelected(false);
      setIsServiceCategorySelected(false);
    }
  }, [serviceCatField, procedureCodeField]);

  const disableProcedureRelated = !isProcedureCodeSelected;

  useEffect(() => {
    setNpi("");
    const providerNpi = searchParams.get("provider_npi");
    const externalMemberID = searchParams.get("external_member_id");
    const dob = searchParams.get("member_date_of_birth");
    const memberFirst = searchParams.get("member_first");
    const memberLast = searchParams.get("member_last");

    if (providerNpi && dob && externalMemberID && memberFirst && memberLast) {
      setNpi(providerNpi);
      const memberGetPayload = {
        provider_npi: providerNpi,
        external_member_id: externalMemberID,
        member_date_of_birth: dob,
        member_first: memberFirst,
        member_last: memberLast,
      };
      findMember(memberGetPayload);
    } else {
      navigate(getThemeURL("PATIENT_LOOKUP"));
    }
  }, []);

  const findMember = useCallback(
    async (data: PatientForm) => {
      setLoading(true);

      try {
        // Find PA Case and process response
        const caseResponse = await getMedicalEligibilityAPI({
          "external_member_id": data.external_member_id,
          "member_date_of_birth": data.member_date_of_birth,
          "member_first_name": data.member_first,
          "member_last_name": data.member_last,
        });
        setMember(caseResponse);
        setLoading(false);

        // analytics log case found
        logEvent(ANALYTIC_EVENTS.MEDICAL.memberFound);

      } catch (error) {
        const params = {
          error: MemberSearchErrors.NOT_FOUND,
          provider_npi: data.provider_npi,
          external_member_id: data.external_member_id,
          member_date_of_birth: data.member_date_of_birth,
          member_first: data.member_first,
          member_last: data.member_last,
        };

        const encodedParams = new URLSearchParams();
        for (const key in params) {
          if (params[key]) {
            encodedParams.append(key, params[key]);
          }
        }
        // analytics log case not found
        logEvent(ANALYTIC_EVENTS.ERRORS.memberNotFound);

        navigate(`${getThemeURL("PATIENT_LOOKUP")}?${encodedParams.toString()}`);
      }
    },
    [navigate],
  );

  const [resultsLoading, setResultsLoading] = useState<boolean>(false);

  const handleSearch = useCallback(async (values: BenefitSearchForm): Promise<void> => {
    setError(undefined);
    setEmptyResultMessage(null);
    setResultsLoading(true);

    if (!values.procedure_code && !values.service_category) {
      setError("Select either a Service Category or a Procedure Code to proceed");
      setResultsLoading(false);
      return;

    } else if (values.service_category) {
      const payload: MedicalEligibilityRequest = {
        "external_member_id": member.external_member_id,
        "member_date_of_birth": member.member_date_of_birth,
        "member_first_name": member.member_first_name,
        "member_last_name": member.member_last_name,
        "service_category": values.service_category,
      };
      const isSamePayload = JSON.stringify(payload) === JSON.stringify(lastPayload);
      if (isSamePayload) {
        setResultsLoading(false);
        return;
      }
      try {
        const response = await getMedicalEligibilityAPI(payload);

        const serviceMaximums = response?.service_maximums;
        setServiceCategory(response?.service_category);

        const isEmptyResult = !serviceMaximums || Object.keys(serviceMaximums).length === 0;

        if (isEmptyResult) {
          setEmptyResultMessage("Service not found");
          setServiceMaximumResults({});
          return;
        }

        const flatData: FlattenedServiceData[] = flattenServiceData(serviceMaximums);

        setServiceMaximumResults(flatData);
        setLastPayload(payload);
        form.reset();
      } catch (err) {
        setError("An error occurred while checking coverage. Please try again.");
        setServiceMaximumResults({});
      } finally {
        setResultsLoading(false);
      }

    } else {
      setDiagnosisCodes(values.diagnosis_codes);

      const payload: MedicalBenefitRequest = {
        procedure_code: values.procedure_code,
        diagnosis_codes: values.diagnosis_codes.map((code) => code.trim()),
        place_of_service_code: values.place_of_service,
        service_category: values.service_category,
        service_date: new Date().toISOString().split("T")[0],
        member_id: member?.member_id,
        client_id: member?.client_id,
        provider_npi: npi,
      };

      const isSamePayload = JSON.stringify(payload) === JSON.stringify(lastPayload);
      if (isSamePayload) {
        setResultsLoading(false);
        return;
      }

      try {
        const response = await createMedicalClaim(payload);
        const isEmptyResult = response && Object.keys(response).length === 0;

        if (isEmptyResult) {
          setEmptyResultMessage("Service not found");
          return;
        }

        setResults((prevResults) => [response, ...prevResults]);
        setLastPayload(payload);
      } catch (err) {
        setError("An error occurred while checking coverage. Please try again.");
      } finally {
        setResultsLoading(false);
      }
      form.reset();
    }
  }, [member, lastPayload, npi]);

  useEffect(() => {
    if (member && !loading) {
      const today = new Date();
      const start = new Date(member.coverage_start);
      const end = new Date(member.coverage_end);

      if (today >= start && today <= end) {
        setTag(<Tag type="success-high-contrast">Active Coverage</Tag>);
      } else if (today < start) {
        setTag(<Tag type="warning-high-contrast">Future Coverage</Tag>);
      } else if (today > end) {
        setTag(<Tag type="error-high-contrast">Termed Coverage</Tag>);
      }
    }
  }, [member, loading]);

  const handlerSelectedTabChange = (selectedTabId: string | null) => {
    if (!selectedTabId) {
      return;
    }

    if (tabOptions.some((option) => option.id === selectedTabId)) {
      setSelectedTab(selectedTabId);
      setSearchParams(getEncodedMemberParams(selectedTabId), { replace: true });
      setIsMobileTabsExpanded(false);
    }
  };

  useEffect(() => {
    if (tabOptions.length === 0) {
      return;
    }

    if (!selectedTab || !tabOptions.some((option) => option.id === selectedTab)) {
      setSelectedTab(tabOptions[0].id);
    }
  }, [selectedTab, tabOptions]);

  useEffect(() => {
    const fetchCases = async () => {
      if (!isUserLoggedIn()) {
        setHasActiveAccount(false);
        return;
      }

      /* User's provider record must be active status
            should we tell the user this with some verbiage? */
      const user = await getUser();
      const activeAccount = user.provider_enrollment_status === "active";
      setHasActiveAccount(activeAccount);

      if (activeAccount) {
        setLoading(true);
        try {
          const authToken = getAuthToken();
          const requestPayload = {
            is_action_required: false,
            results_per_page: 10,
            page_number: pageNumber,
          };
          const response = await getPACaseList(requestPayload, authToken);
          setPAResults(response?.results || []);
        } catch (error) {
          console.error("Failed to fetch cases:", error);
        } finally {
          setLoading(false);
        };
      };
    };

    fetchCases();
  }, [pageNumber, isUserLoggedIn, getUser]);

  return (
    <div className={styles.BenefitSearchContainer} style={{ width: layoutWidth, maxWidth: "100%", margin: "0 auto" }}>
      {loading && (
        <Stack className={styles.LoadingContainer} alignment="center">
          <Heading className={styles.LoadingText}>Finding patient...</Heading>
          <Spinner className={styles.LoadingSpinner} size="large" />
        </Stack>
      )}
      {(!loading && member) && (
        <Stack spacing={isCompressed ? "none" : "medium-large"}>
          <Stack
            spacing="small"
            alignment="left"
            className={isCompressed ? styles.MemberHeaderMobile : styles.SectionSpacer}
          >
            <Title>{member.member_first_name} {member.member_last_name}</Title>
            <Layout spacing={isCompressed ? "small" : "medium"}>
              <Tooltip alignment="top" content="Member ID">{(ref) => <div ref={ref}><Body size="small">{member.external_member_id}</Body></div>}</Tooltip>
              <Tooltip alignment="top" content="Plan Name">{(ref) => <div ref={ref}><Body size="small">{member.plan_name}</Body></div>}</Tooltip>
              <Layout spacing={isCompressed ? "mini" : "small"}>
                <Tooltip alignment="top" content="Coverage Dates">
                  {(ref) => <div ref={ref}><Body size="small">{(member.coverage_start)} - {(member.coverage_end)}</Body></div>}
                </Tooltip>
                <Shelf>
                  {tag}
                  <Shelf.Spacer />
                </Shelf>
                <Shelf.Spacer />
              </Layout>
            </Layout>
          </Stack>
          <Form form={form} onSubmit={handleSearch}>
            {isCompressed ? (
              <div className={styles.MobileTabDropdown}>
                <button
                  type="button"
                  className={styles.MobileTabSelector}
                  onClick={() => setIsMobileTabsExpanded((expanded) => !expanded)}
                >
                  <span className={styles.MobileTabTitleText}>{selectedTabLabel}</span>
                  <Icon
                    name={isMobileTabsExpanded ? "chevron-up" : "chevron-down"}
                    size="regular"
                    className={styles.MobileTabCaret}
                  />
                </button>
                {isMobileTabsExpanded && (
                  <Stack spacing="none" className={styles.MobileTabOptions}>
                    {tabOptions.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        className={styles.MobileTabOption}
                        onClick={() => handlerSelectedTabChange(option.id)}
                      >
                        <Body>{option.label}</Body>
                      </button>
                    ))}
                  </Stack>
                )}
              </div>
            ) : (
              <TabSet selectedTab={selectedTab} onSelectedTabChange={handlerSelectedTabChange}>
                {tabOptions.map((option) => (
                  <TabSet.Tab key={option.id} id={option.id}><Subheading>{option.label}</Subheading></TabSet.Tab>
                ))}
              </TabSet>
            )}
            <Stack.Spacer />
            {/* Coverage Check Tab */}
            {featureFlagHideProdAndPrime && selectedTab === "coverage-check" && (
              <Stack style={{ paddingTop: "32px" }} spacing="large">
                <Stack>
                  <Grid>
                    <Grid.Span columns={fourFull}>
                      <ServiceCategoryDropdown name="service_category"
                        disabled={isProcedureCodeSelected}
                        plan_id={member?.plan_id}
                        external_member_id={member?.external_member_id}
                        person_code={member?.person_code}
                        member_id={member?.member_id}
                      />
                    </Grid.Span>
                  </Grid>
                  <Body>or</Body>
                  <Grid>
                    <Grid.Span columns={fourFull}>
                      <ProcedureCodeDropdown name="procedure_code" disabled={isServiceCategorySelected} />
                    </Grid.Span>
                    <Grid.Span columns={fourFull}>
                      <DiagnosisCodeMultiSelect name="diagnosis_codes" required disabled={disableProcedureRelated} />
                    </Grid.Span>
                    <Grid.Span columns={fourFull}>
                      <Dropdown.Field
                        required
                        name="place_of_service"
                        label="Place of Service"
                        disabled={disableProcedureRelated}
                      >
                        {placeOfServiceOptions.map((options, index) => (
                          <Dropdown.Option key={index} value={options.value} label={options.label}>
                            {options.label}
                          </Dropdown.Option>
                        ))}
                      </Dropdown.Field>
                    </Grid.Span>
                  </Grid>
                  <Shelf>
                    <SubmitButton appearance={getThemeButton("primary", primary_color)}>Check Service</SubmitButton>
                    <Shelf.Spacer />
                  </Shelf>
                </Stack>
                {error && <Message appearance={Messages.Error} visible>{error}</Message>}
                {resultsLoading && (
                  <Stack alignment="center" className={styles.LoadingContainer}>
                    <Spinner size="large" />
                    <Heading className={styles.LoadingText}>Checking coverage...</Heading>
                  </Stack>
                )}
                {!resultsLoading && emptyResultMessage && (
                  <Message appearance={Messages.Error} visible>
                    {emptyResultMessage}
                  </Message>
                )}
                <Stack>
                  {!resultsLoading && !emptyResultMessage && (results.length > 0 || serviceMaximumResults.length > 0) && (
                    <Message
                      appearance={Messages.Informational}
                      visible
                    >
                      Verification of benefits or coverage is not a guarantee of eligibility or payment. Actual payment is
                      based on the terms and conditions of the plan. All claims are subject to review upon submission. All
                      benefits quoted reflect in-network level benefits unless otherwise requested.
                    </Message>
                  )}
                  {results.length > 0 && (
                    <>
                      {results.map((result, index) => (
                        <ResultPanel
                          key={index}
                          results={result}
                          diagnosis_codes={diagnosis_codes}
                        />
                      ))}
                    </>
                  )}
                  {serviceMaximumResults.length > 0 && (
                    <>
                      {serviceMaximumResults.map((item, index) => (
                        <ServiceMaxResultPanel
                          key={index}
                          service_category={serviceCategory}
                          service_maximum={item}
                        />
                      ))}
                    </>
                  )}
                </Stack>
              </Stack>
            )}
            {/* Accumulations Tab */}
            {featureFlagHideProdAndPrime && selectedTab === "accumulations" && (
              <Stack style={{ paddingTop: "32px" }}>
                {member?.accumulations?.length > 0 ? (
                  <Accumulations accumulations={member.accumulations} />
                ) : (
                  <Body>No results found</Body>
                )}
              </Stack>
            )}
            {/* Drug Coverage Tab */}
            {selectedTab === "drug-coverage" && (
              <Stack style={{ paddingTop: "32px" }}>
                <DrugCoverage member={member} />
              </Stack>
            )}
            {/* PA Cases Tab */}
            {selectedTab === "pa-cases" && (
              <Stack style={{ paddingTop: "32px" }}>
                {!loading && paResults &&
                  <Stack>
                    {hasActiveAccount ?
                      <PACaseListSearch pageNumber={pageNumber} setPageNumber={setPageNumber} externalMemberID={[member.external_member_id]} patientDOB={member?.member_date_of_birth} returnUrl={paCaseListUrl} />
                      :
                      <PACaseSearch
                        external_member_id={member?.external_member_id}
                        prescriber_npi={npi}
                        member_date_of_birth={member?.member_date_of_birth}
                        returnUrl={paCaseListUrl}
                      />
                    }
                    <Stack.Spacer />
                  </Stack>

                }
              </Stack>
            )}
          </Form>
        </Stack>
      )}
    </div>
  );
};

export default BenefitSearchForm;
